import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight } from '@iwoca/icons';
import { Link } from '@iwoca/orion';
import { useSearchParams } from 'react-router-dom';

import BankConnectionStep from './BankConnectionStep';
import BankSelectionStep from './BankSelectionStep';
import styles from './LinkBankAccount.module.css';
import { useGetContext } from '../../../../../api/lendingApi/lendingApiQueries';
import { useTruelayerAuthorizationMutation } from '../../../../../api/openBankingLinkApi/usePostTruelayerProviderAuthorizationMutation';
import { useContextStateKey } from '../../../../../hooks/useContextStateKey';
import { useSplitTestContext } from '../../../../../hooks/useSplitTestContext';
import { getAmplitudeDefaultEventProperties } from '../../../../../utils/amplitudeEvents';
import ShareableLinkModal from '../../../OpenBanking/components/ShareableLinkModal';
import { TOnlineBank } from '../../../OpenBanking/OpenBanking.types';
import {
  getMigratedProviderName,
  TLinkedBankAccount,
} from '../../../OpenBanking/trueLayer.helpers';
import TrueLayerAlertBox from '../../../OpenBanking/TrueLayerAlertBox/TrueLayerAlertBox';
import TrueLayerLinkedAccount from '../../../OpenBanking/TrueLayerLinkedAccount/TrueLayerLinkedAccount';
import BankStatementUploadRevamp from '../../BankStatementUpload/BankStatementUploadRevamp';

enum onlineBankSelection {
  BANK_LIST = 'bankList',
  BANK_CONNECTION = 'bankConnection',
}

type TProps = {
  linkedBankAccount?: TLinkedBankAccount[];
  isActive: boolean;
  onRequestUpload: () => void;
  showDirectUpload?: boolean;
};

const LinkBankAccount = ({
  linkedBankAccount,
  isActive,
  onRequestUpload,
  showDirectUpload,
}: TProps) => {
  const [view, setView] = useState<onlineBankSelection>(
    onlineBankSelection.BANK_LIST,
  );
  const [selectedBank, setSelectedBank] = useState<TOnlineBank | null>(null);
  const [showBankSelection, setShowBankSelection] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [providerMigration, setProviderMigration] = useState<{
    successorBankId?: string;
  } | null>(null);

  const [searchParams] = useSearchParams();
  const stateKey = useContextStateKey();
  const { context } = useGetContext();
  const { hasOpenBankingShareableLinkEnabled } = useSplitTestContext();
  const { postBankAuthentication, bankAuthUrl, isError, resetURL } =
    useTruelayerAuthorizationMutation();

  const hasLinkedAccounts = linkedBankAccount && linkedBankAccount.length > 0;

  useEffect(() => {
    if (!isActive) {
      setShowBankSelection(false);
    }
  }, [isActive]);

  useEffect(() => {
    const statusCode = searchParams.get('status_code');
    const errorDetail = searchParams.get('error_detail');
    const subCode = searchParams.get('sub_code');

    if (!statusCode) return;

    setProviderMigration(null);

    if (statusCode === '200') {
      tracking.ampli.openBankingLinkedSuccessfully();
    } else if (statusCode === '500') {
      // ProviderMigrated takes precedence over access_denied, matching the
      // default arm (error_detail carries the successor bank id).
      if (subCode === 'ProviderMigrated') {
        tracking.ampli.openBankingLinkingFailed({
          reason: 'provider_migrated',
        });
        setProviderMigration({ successorBankId: errorDetail ?? undefined });
      } else if (errorDetail === 'access_denied') {
        tracking.ampli.openBankingLinkingCancelled();
      } else {
        tracking.ampli.openBankingLinkingFailed({ reason: 'unknown' });
      }
    }
  }, [searchParams]);

  const handleActivateStepForUpload = () => {
    setShowBankSelection(false);
    onRequestUpload();
  };

  const handleBankSelect = (bank: TOnlineBank) => {
    if (!stateKey || !bank.provider) return;

    tracking.ampli.trueLayerBankSelection({
      bankId: bank.bank_id,
      bankName: bank.bank_name,
    });

    setSelectedBank(bank);
    setView(onlineBankSelection.BANK_CONNECTION);

    postBankAuthentication({
      stateKey,
      providerId: bank.bank_id,
    });
  };

  const handleBackClick = () => {
    tracking.ampli.backCtaClicked({
      ...getAmplitudeDefaultEventProperties(),
      'CTA text': 'Back',
    });
    resetURL();
    setView(onlineBankSelection.BANK_LIST);
  };

  const shouldShowLinkOpenBankingSplitTest =
    !hasLinkedAccounts || showBankSelection || !!providerMigration;

  useEffect(() => {
    if (shouldShowLinkOpenBankingSplitTest && !hasLinkedAccounts) {
      tracking.ampli.openBankingConnectStarted({
        ...getAmplitudeDefaultEventProperties(),
      });
    }
  }, [shouldShowLinkOpenBankingSplitTest]);

  return (
    <>
      {hasLinkedAccounts && (
        <div className={styles.linkedAccountWrapper}>
          <TrueLayerLinkedAccount linkedBankAccount={linkedBankAccount} />
        </div>
      )}
      {hasLinkedAccounts && !showBankSelection && !providerMigration && (
        <button
          className={styles.additionalAccountLink}
          onClick={() => {
            setShowBankSelection(true);
            onRequestUpload();
          }}
        >
          Link an additional account +
        </button>
      )}
      {shouldShowLinkOpenBankingSplitTest && (
        <div className={styles.container}>
          {view === onlineBankSelection.BANK_LIST && (
            <>
              <BankSelectionStep onBankSelect={handleBankSelect} />
              {hasOpenBankingShareableLinkEnabled && (
                <div className={styles.helpLinkContainer}>
                  <Link
                    className={styles.helpLink}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Don’t know your login and need help linking your account?
                    <IconAngleRight className={styles.helpLinkIcon} />
                  </Link>
                </div>
              )}
              <ShareableLinkModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
              />
            </>
          )}
          {view === onlineBankSelection.BANK_CONNECTION && selectedBank && (
            <BankConnectionStep
              onClickBack={handleBackClick}
              selectedBank={selectedBank}
              bankAuthURL={bankAuthUrl ?? ''}
              bankUrlError={isError}
            />
          )}
        </div>
      )}
      {providerMigration && (
        <TrueLayerAlertBox
          status="provider_migrated"
          successorName={getMigratedProviderName(
            context,
            providerMigration.successorBankId,
          )}
        />
      )}
      <BankStatementUploadRevamp
        isActive={isActive && !showBankSelection}
        onRequestUpload={handleActivateStepForUpload}
        showDirectUpload={showDirectUpload}
      />
    </>
  );
};

export default LinkBankAccount;
